import { useEffect, useRef } from "react";
import { CognitoAuthService } from "../services/cognitoService";
import { notifySessionUnauthorized } from "../auth/sessionUnauthorizedEvents";
import { setStoredIdToken } from "../auth/tokenStore";
import { debug } from "../utils/debugLogger";

interface UseTokenRefreshOptions {
  isAuthenticated: boolean;
  /** Current ID token — used only to read its `exp` and schedule the first refresh. */
  idToken: string;
  clearAuthState: () => void;
}

// Refresh this long BEFORE the token's exp so we never serve an expired token.
const REFRESH_SKEW_MS = 2 * 60 * 1000; // 2 minutes
// Never schedule a refresh sooner than this (guards against exp at/near now).
const MIN_DELAY_MS = 30 * 1000; // 30 seconds
// If exp can't be read from the token, fall back to a sane periodic refresh.
const FALLBACK_DELAY_MS = 30 * 60 * 1000; // 30 minutes
const RETRY_DELAYS_MS = [5_000, 15_000, 45_000];

/**
 * Treat true auth failures (refresh token expired/invalid/revoked) as session-end,
 * but classify network/timeout/5xx as transient so a Wi-Fi blip doesn't boot the user.
 */
function isUnrecoverableAuthError(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  const err = error as { name?: string; code?: string; message?: string };
  const msg = (err.message || "").toLowerCase();
  const code = (err.code || err.name || "").toLowerCase();
  return (
    code.includes("notauthorizedexception") ||
    code.includes("usernotfoundexception") ||
    code.includes("passwordresetrequired") ||
    code.includes("userdisabledexception") ||
    msg.includes("refresh token") ||
    msg.includes("token is expired") ||
    msg.includes("invalid refresh token") ||
    msg.includes("user is not authenticated")
  );
}

/** Milliseconds from now until the token's `exp`, or null if it can't be parsed. */
function msUntilExpiry(idToken: string): number | null {
  try {
    const payload = CognitoAuthService.parseTokenPayload(idToken);
    if (payload && typeof payload.exp === "number") {
      return payload.exp * 1000 - Date.now();
    }
  } catch {
    /* unparseable — caller falls back */
  }
  return null;
}

/**
 * Refresh the Cognito ID token shortly before it expires — NOT on a fixed
 * interval. We read the token's `exp` and schedule a single timer to refresh
 * ~2 minutes ahead of expiry, then reschedule from the new token. This avoids
 * the wasteful 10-minute polling that previously ran 6x more often than needed.
 *
 * Transient failures (network/5xx) retry with backoff; only unrecoverable auth
 * errors clear state and prompt re-login.
 *
 * On success the rotated token is written to the module token store ONLY — it is
 * deliberately NOT pushed into React state. ~74 effects across the app depend on
 * AuthContext.idToken; updating it on every routine refresh re-fires them all at
 * once (the whole-page "refresh" users reported). Request-time helpers (the axios
 * interceptor and authedFetch) read the fresh token from the store instead.
 */
export const useTokenRefresh = ({
  isAuthenticated,
  idToken,
  clearAuthState,
}: UseTokenRefreshOptions) => {
  const consecutiveFailuresRef = useRef(0);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Keep the latest values in refs so the scheduler reads fresh data without
  // re-running the effect — re-running would reset the timer on every render.
  const idTokenRef = useRef(idToken);
  idTokenRef.current = idToken;
  const clearAuthStateRef = useRef(clearAuthState);
  clearAuthStateRef.current = clearAuthState;

  useEffect(() => {
    if (!isAuthenticated) return;

    let cancelled = false;

    const clearTimer = () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };

    // Schedule the next refresh based on a token's expiry.
    const scheduleFromToken = (token: string) => {
      if (cancelled) return;
      clearTimer();
      const untilExp = msUntilExpiry(token);
      const delay =
        untilExp == null
          ? FALLBACK_DELAY_MS
          : Math.max(untilExp - REFRESH_SKEW_MS, MIN_DELAY_MS);
      // TEST-ONLY: uncomment to force the refresh to fire every 15s and observe
      // the no-cascade behavior without waiting ~58 min. NEVER commit enabled.
      // const delay = 15_000;
      debug.log(`Token refresh scheduled in ~${Math.round(delay / 1000)}s`);
      timeoutRef.current = setTimeout(attemptRefresh, delay);
    };

    const attemptRefresh = async () => {
      if (cancelled) return;
      try {
        const session = await CognitoAuthService.getCurrentSession();

        if (session) {
          consecutiveFailuresRef.current = 0;
          // Persist to the module store (and localStorage) WITHOUT touching React
          // state — this is what keeps the routine refresh invisible.
          setStoredIdToken(session.idToken);
          // Reschedule from the freshly-issued token's expiry.
          scheduleFromToken(session.idToken);
          return;
        }

        // No session — Cognito returned cleanly with null. Treat as session-end.
        debug.warn("Token refresh: no session returned by Cognito");
        notifySessionUnauthorized(401);
        clearAuthStateRef.current();
      } catch (error) {
        if (isUnrecoverableAuthError(error)) {
          debug.error("Token refresh: unrecoverable auth error", error);
          notifySessionUnauthorized(401);
          clearAuthStateRef.current();
          return;
        }

        // Transient — retry with backoff. Do NOT boot the user.
        consecutiveFailuresRef.current += 1;
        const attempt = consecutiveFailuresRef.current;
        debug.warn(
          `Token refresh: transient failure (attempt ${attempt}, ${RETRY_DELAYS_MS.length} retries available)`,
          error
        );

        // All retry slots used and still failing → give up
        if (attempt > RETRY_DELAYS_MS.length) {
          debug.error(
            "Token refresh: exhausted retries after consecutive transient failures"
          );
          notifySessionUnauthorized(401);
          clearAuthStateRef.current();
          return;
        }

        clearTimer();
        timeoutRef.current = setTimeout(
          attemptRefresh,
          RETRY_DELAYS_MS[attempt - 1]
        );
      }
    };

    // Initial schedule from the current token's expiry (no immediate network call).
    scheduleFromToken(idTokenRef.current);

    return () => {
      cancelled = true;
      clearTimer();
    };
  }, [isAuthenticated]);
};
